/**
 * RWBR — RARE results, plus the SUPER RARE and BLACK FLAG tables that chain
 * from them.
 *
 * RULE (rulebook p.iv, verbatim):
 *   "On RARE RESULTS, you'll do it the other way around--first you'll roll on
 *    the RARE RESULTS chart to find out what happened, and then, if necessary,
 *    isolate specific drivers.  NOTE that on PROBLEM and RARE results, the race
 *    LEADER is included in the DRIVER FINDER chart as part of the TOP group."
 *
 * So: chart first, driver second — the exact reverse of problem.ts.
 *
 * Chains printed on the RARE RESULTS sheet:
 *   row 13            → "Roll again on SUPER RARE chart."
 *   rows 16/26/36/46/56/66 → "BLACK FLAG ...; see BLACK FLAG table."  The BLACK
 *                       FLAG table is a single d6; its own rule line reads
 *                       "Black-flagged driver sent to end of BOTTOM Group."
 *   SUPER RARE rows 13/33/53/65 carry the † footnote: "Roll one die:
 *                       [1 LEADER][2 TOP][3-4 MIDDLE][5-6 BOTTOM], then use
 *                       driver finder."
 *
 * WHAT THIS MODULE DOES AND DOES NOT DECIDE. Several RARE rows name outcomes
 * whose mechanics are not printed anywhere (rain, race stoppages, quality loss,
 * pit-grade reduction, season-level injuries). Those are returned as
 * `pendingEffects` WITH their drivers already isolated, rather than invented
 * here. Everything the boards do specify — TROUBLE events, black flags, pit
 * errors, driver incidents, surges — is resolved.
 */

import type { Dice } from './dice.ts';
import {
  CHARTS,
  CITE,
  type ChartEffect,
  type ChartEntry,
  type DiceRecord,
  type LedgerEvent,
  type Modifier,
  type PositionDelta,
  type TrackAdjustment,
} from './repair.ts';
import { rollDriverFinder, type DriverFinderResult } from './problem.ts';
import {
  CITE_SUPER_RARE_CAREER,
  CONSEQUENCE_DICE_SIDES,
  type CareerConsequence,
} from './careerConsequences.ts';
import {
  resolveTrouble,
  type ChipPolicy,
  type TroubleCar,
  type TroubleField,
  type TroubleGroup,
  type TroubleResult,
} from './trouble.ts';

const RARE_CHARTS: Record<TroubleGroup, 'rare-results-top' | 'rare-results-middle' | 'rare-results-bottom'> = {
  top: 'rare-results-top',
  middle: 'rare-results-middle',
  bottom: 'rare-results-bottom',
};

const SUPER_RARE = CHARTS['super-rare-results'];
const BLACK_FLAG = CHARTS['black-flag'];

// ---------------------------------------------------------------------------
// Inputs / outputs
// ---------------------------------------------------------------------------

export interface RareChainStep {
  chart: string;
  code: string;
  text: string;
}

/** An effect this module deliberately does not apply, with its drivers resolved. */
export interface PendingEffect {
  effect: ChartEffect;
  actorIds: string[];
  note: string;
}

export interface RareInput {
  field: TroubleField;
  group: TroubleGroup;
  turn: number;
  totalTurns: number;
  escapeAdj?: TrackAdjustment;
  repairAdj?: TrackAdjustment;
  dice: Dice;
  /** THE BIG ONE cascade inside any TROUBLE this row causes. Default ON. */
  bigOne?: boolean;
  chipPolicy?: ChipPolicy;
  /**
   * DRIVER INCIDENT / PIT CREW INCIDENT: "will involve either the driver in the
   * next place ahead or behind, your choice, or you may roll a die to
   * determine." Default 'roll'.
   */
  incidentNeighbor?: 'ahead' | 'behind' | 'roll';
  cause?: string;
}

export interface RareResult extends LedgerEvent {
  eventType: 'rare';
  momentTag: string;
  group: TroubleGroup;
  /** RARE RESULTS row, then SUPER RARE / BLACK FLAG if the row chained. */
  chain: RareChainStep[];
  entryText: string;
  /** Every DRIVER FINDER roll this row required, in order. */
  finders: DriverFinderResult[];
  /** Any TROUBLE event this row set off, fully resolved. */
  trouble: TroubleResult | null;
  /** The d6 infraction, when a BLACK FLAG table roll was made. */
  blackFlagInfraction: { face: number; text: string } | null;
  outIds: string[];
  feudPairs: [string, string][];
  fumeIds: string[];
  yellowFlag: boolean;
  yellowTurns: number;
  chipsForfeited: number;
  pendingEffects: PendingEffect[];
  /**
   * D-EZ-88 — injuries, suspensions and a fatality the SUPER RARE chart hands
   * to the season, with their dice already rolled. See `careerConsequences.ts`.
   */
  careerConsequences: CareerConsequence[];
  fillGap: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KEBAB = (s: string): string =>
  s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase().replace(/^-|-$/g, '');

function allCars(field: TroubleField): TroubleCar[] {
  return [...field.top, ...field.middle, ...field.bottom].sort((a, b) => a.position - b.position);
}

function carAt(field: TroubleField, position: number): TroubleCar | null {
  return allCars(field).find((c) => c.position === position) ?? null;
}

/** † footnote on SUPER RARE: [1 LEADER][2 TOP][3-4 MIDDLE][5-6 BOTTOM]. */
function groupFromFootnoteDie(face: number): TroubleGroup | 'leader' {
  if (face === 1) return 'leader';
  if (face === 2) return 'top';
  if (face <= 4) return 'middle';
  return 'bottom';
}

/** An explicit `group` on any effect of the row, e.g. `disqualify: {group}`. */
function groupFromEffects(effects: ChartEffect[]): TroubleGroup | null {
  for (const e of effects) {
    if (typeof e.group === 'string') {
      const g = e.group.toLowerCase();
      if (g === 'top' || g === 'middle' || g === 'bottom') return g;
    }
  }
  return null;
}

/**
 * SUPER RARE rows 23 and 52 name their group in the printed text only
 * ("Driver in MIDDLE group falls ill behind the wheel") and carry a bare
 * `outOfRace` effect. Read the group out of that exact phrase — and ONLY that
 * phrase, because plenty of rows mention a group as a DESTINATION ("moves to
 * end of BOTTOM group"), which is not the group to search.
 */
function groupFromDriverInPhrase(text: string): TroubleGroup | null {
  const m = /driver in (?:the )?(TOP|MIDDLE|BOTTOM) group/i.exec(text);
  return m ? (m[1].toLowerCase() as TroubleGroup) : null;
}

interface Isolation {
  car: TroubleCar | null;
  finder: DriverFinderResult | null;
  dice: DiceRecord[];
  modifiers: Modifier[];
}

/** p.iv — "isolate specific drivers" via the DRIVER FINDER for a group. */
function isolate(group: TroubleGroup, input: RareInput): Isolation {
  const finder = rollDriverFinder(group, input.field, input.dice);
  return { car: finder.car, finder, dice: [finder.dice], modifiers: finder.modifiers };
}

function isolateLeaderOrGroup(target: TroubleGroup | 'leader', input: RareInput): Isolation {
  if (target === 'leader') {
    const car = carAt(input.field, 1);
    return {
      car,
      finder: null,
      dice: [],
      modifiers: [{ name: 'driver named by footnote die', value: 'race LEADER', detail: CITE.leaderIsTop }],
    };
  }
  return isolate(target, input);
}

/**
 * The neighbouring car for a DRIVER / PIT CREW INCIDENT.
 *
 * AMBIGUITY: the printed procedure says "the driver in the next place ahead or
 * behind, your choice, or you may roll a die to determine" but never prints the
 * die mapping. Implemented as 1-3 = ahead, 4-6 = behind — the same even split
 * the sheet uses elsewhere for two-way choices (PIT ERROR's 1-3 / 4-5 / 6 is
 * three-way, so it is no guide). Callers who care should pass an explicit
 * 'ahead' or 'behind'.
 */
function neighbourOf(
  car: TroubleCar,
  input: RareInput,
): { car: TroubleCar | null; dice: DiceRecord[]; modifiers: Modifier[] } {
  const cars = allCars(input.field);
  const idx = cars.findIndex((c) => c.driverId === car.driverId);
  const ahead = idx > 0 ? cars[idx - 1] : null;
  const behind = idx >= 0 && idx < cars.length - 1 ? cars[idx + 1] : null;

  const mode = input.incidentNeighbor ?? 'roll';
  if (mode === 'ahead') {
    return { car: ahead ?? behind, dice: [], modifiers: [{ name: 'incident partner', value: 'next place ahead' }] };
  }
  if (mode === 'behind') {
    return { car: behind ?? ahead, dice: [], modifiers: [{ name: 'incident partner', value: 'next place behind' }] };
  }
  const face = input.dice.d6();
  const pick = face <= 3 ? (ahead ?? behind) : (behind ?? ahead);
  return {
    car: pick,
    dice: [{ kind: 'd6', dice: [face], label: 'incident partner (1-3 ahead, 4-6 behind)' }],
    modifiers: [{ name: 'incident partner', value: face <= 3 ? 'next place ahead' : 'next place behind', detail: `d6 ${face}` }],
  };
}

function troubleFor(
  groups: TroubleGroup | TroubleGroup[],
  input: RareInput,
  rollsPerDriver: number,
  cause: string,
): TroubleResult {
  return resolveTrouble({
    field: input.field,
    group: groups,
    turn: input.turn,
    totalTurns: input.totalTurns,
    escapeAdj: input.escapeAdj,
    repairAdj: input.repairAdj,
    dice: input.dice,
    bigOne: input.bigOne,
    chipPolicy: input.chipPolicy,
    rollsPerDriver,
    cause,
  });
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a RARE result: chart first (p.iv), then isolate whoever it names.
 */
export function resolveRare(input: RareInput): RareResult {
  const { dice } = input;
  const chartId = RARE_CHARTS[input.group];
  const chart = CHARTS[chartId];

  const roll = dice.code1166();
  const entry: ChartEntry | undefined = chart.entries[roll.code];
  if (!entry) throw new RangeError(`${chartId}: no entry for code ${roll.code}`);

  const diceRecords: DiceRecord[] = [
    { kind: '11-66', dice: [roll.red, roll.white], code: roll.code, label: chart.name },
  ];
  const modifiers: Modifier[] = [
    { name: 'RARE RESULTS chart', value: chart.name },
    { name: 'row rolled (11-66)', value: roll.code, detail: entry.text },
  ];
  const chain: RareChainStep[] = [{ chart: chart.name, code: roll.code, text: entry.text }];

  // --- SUPER RARE chain (row 13) --------------------------------------------
  let active = entry;
  let sourceChart = chart.name;
  let sourceCitation = chart.sourceCitation;
  if ((entry.effects ?? []).some((e) => e.type === 'rerollOn' && e.chart === 'super-rare-results')) {
    const srRoll = dice.code1166();
    const srEntry = SUPER_RARE.entries[srRoll.code];
    if (!srEntry) throw new RangeError(`super-rare-results: no entry for code ${srRoll.code}`);
    diceRecords.push({ kind: '11-66', dice: [srRoll.red, srRoll.white], code: srRoll.code, label: SUPER_RARE.name });
    modifiers.push({ name: 'SUPER RARE row', value: srRoll.code, detail: srEntry.text });
    chain.push({ chart: SUPER_RARE.name, code: srRoll.code, text: srEntry.text });
    active = srEntry;
    sourceChart = SUPER_RARE.name;
    sourceCitation = SUPER_RARE.sourceCitation;
  }

  // The † footnote fixes the group BEFORE the driver finder runs.
  let footnoteTarget: TroubleGroup | 'leader' | null = null;
  if (active.footnote === '†') {
    const face = dice.d6();
    footnoteTarget = groupFromFootnoteDie(face);
    diceRecords.push({ kind: 'd6', dice: [face], label: '† group die [1 LEADER][2 TOP][3-4 MIDDLE][5-6 BOTTOM]' });
    modifiers.push({ name: '† group die', value: face, detail: `→ ${footnoteTarget.toUpperCase()}` });
  }

  const finders: DriverFinderResult[] = [];
  const positionDeltas: PositionDelta[] = [];
  const actorIds: string[] = [];
  const outIds: string[] = [];
  const feudPairs: [string, string][] = [];
  const fumeIds: string[] = [];
  const pendingEffects: PendingEffect[] = [];
  const careerConsequences: CareerConsequence[] = [];
  let trouble: TroubleResult | null = null;
  let blackFlagInfraction: { face: number; text: string } | null = null;
  let yellowFlag = false;
  let yellowTurns = 0;
  let chipsForfeited = 0;
  let fillGap = false;
  let momentTag: string | null = null;

  const noteActor = (car: TroubleCar | null): void => {
    if (car && !actorIds.includes(car.driverId)) actorIds.push(car.driverId);
  };

  /**
   * ONE row names ONE driver. SUPER RARE 11 is "BLACK FLAG + immediate DQ for
   * driver in TOP group" — one man, two consequences, so the DRIVER FINDER is
   * rolled once and the result reused across the row's effects. Only a row that
   * explicitly names two drivers (the FIGHT rows) asks for a fresh isolation.
   */
  const rowTarget: TroubleGroup | 'leader' =
    footnoteTarget ??
    groupFromEffects(active.effects ?? []) ??
    groupFromDriverInPhrase(active.text) ??
    input.group;

  let isolated: TroubleCar | null = null;
  let isolatedOnce = false;

  const takeIsolation = (fresh = false): TroubleCar | null => {
    if (!fresh && isolatedOnce) return isolated;
    const iso = isolateLeaderOrGroup(rowTarget, input);
    if (iso.finder) finders.push(iso.finder);
    diceRecords.push(...iso.dice);
    modifiers.push(...iso.modifiers);
    noteActor(iso.car);
    if (!fresh) {
      isolated = iso.car;
      isolatedOnce = true;
    }
    return iso.car;
  };

  /** A later, harsher placement replaces an earlier one for the same driver. */
  const pushDelta = (delta: PositionDelta): void => {
    const i = positionDeltas.findIndex((d) => d.driverId === delta.driverId);
    if (i === -1) {
      positionDeltas.push(delta);
      return;
    }
    if (positionDeltas[i].placement === 'out-of-race') return;
    positionDeltas[i] = delta;
  };

  const sendToEndOfBottom = (car: TroubleCar, why: string): void => {
    pushDelta({ driverId: car.driverId, from: car.position, to: null, placement: 'end-of-bottom', note: why });
    chipsForfeited += car.chips ?? 0;
    if (car.position < 20) fillGap = true;
  };

  const sendOut = (car: TroubleCar, why: string): void => {
    pushDelta({ driverId: car.driverId, from: car.position, to: null, placement: 'out-of-race', note: why });
    if (!outIds.includes(car.driverId)) outIds.push(car.driverId);
    chipsForfeited += car.chips ?? 0;
    if (car.position < 20) fillGap = true;
  };

  const effectGroup = (effect: ChartEffect): TroubleGroup =>
    typeof effect.group === 'string' ? (effect.group.toLowerCase() as TroubleGroup) : input.group;

  for (const effect of active.effects ?? []) {
    switch (effect.type) {
      case 'rerollOn': {
        if (effect.chart === 'super-rare-results') break; // handled above
        if (effect.chart === 'black-flag') {
          const face = dice.d6();
          const bf = BLACK_FLAG.entries[String(face)];
          blackFlagInfraction = { face, text: bf?.text ?? '' };
          diceRecords.push({ kind: 'd6', dice: [face], label: BLACK_FLAG.name });
          modifiers.push({ name: 'BLACK FLAG infraction', value: face, detail: bf?.text ?? '' });
          chain.push({ chart: BLACK_FLAG.name, code: String(face), text: bf?.text ?? '' });
          break;
        }
        pendingEffects.push({ effect, actorIds: [], note: `unhandled re-roll target "${String(effect.chart)}"` });
        break;
      }

      case 'blackFlag': {
        momentTag ??= 'rare.black-flag';
        const car = takeIsolation();
        if (car) sendToEndOfBottom(car, 'BLACK FLAG — "Black-flagged driver sent to end of BOTTOM Group."');
        break;
      }

      case 'surge': {
        momentTag ??= 'rare.surge';
        const car = takeIsolation();
        if (!car) break;
        const to = String(effect.to ?? 'LEAD');
        if (to === 'LEAD') {
          positionDeltas.push({ driverId: car.driverId, from: car.position, to: 1, placement: 'lead' });
          const leader = carAt(input.field, 1);
          if (leader && leader.driverId !== car.driverId) {
            // "Move the displaced driver to the surging driver's previous
            // position, or the TOP of the BOTTOM group, whichever is higher."
            const displacedTo = Math.min(car.position, 20);
            positionDeltas.push({
              driverId: leader.driverId,
              from: 1,
              to: displacedTo,
              placement: 'hold',
              note: 'displaced by the SURGE',
            });
          }
        } else {
          // "Otherwise, roll one die to determine which driver in the TOP group
          // is displaced."
          const face = dice.d6();
          diceRecords.push({ kind: 'd6', dice: [face], label: 'SURGE — displaced driver' });
          // AMBIGUITY: the printed SURGE procedure only describes displacing a
          // TOP group driver. Row 21 of the BOTTOM column reads "Driver moves to
          // MIDDLE group!", for which no displacement rule is printed. Read here
          // as the same die against the destination group's board layout, taking
          // the odd (higher) side of a MIDDLE box.
          const displacedPos = to === 'TOP' ? face + 1 : 7 + 2 * face;
          const displaced = carAt(input.field, displacedPos);
          positionDeltas.push({ driverId: car.driverId, from: car.position, to: displacedPos, placement: to === 'TOP' ? 'top-group' : 'middle-group' });
          if (displaced) {
            const displacedTo = Math.min(car.position, 20);
            positionDeltas.push({
              driverId: displaced.driverId,
              from: displacedPos,
              to: displacedTo,
              placement: 'hold',
              note: 'displaced by the SURGE',
            });
            noteActor(displaced);
          }
        }
        fillGap = true;
        break;
      }

      case 'moveToEndOfBottom': {
        momentTag ??= `rare.${KEBAB(active.text.split(/[;,.]/)[0] ?? 'demotion')}`;
        const car = takeIsolation();
        if (car) sendToEndOfBottom(car, active.text);
        break;
      }

      case 'pitError': {
        momentTag ??= 'rare.pit-error';
        const car = takeIsolation();
        if (car) {
          sendToEndOfBottom(car, 'PIT ERROR');
          fumeIds.push(car.driverId);
          const face = dice.d6();
          const detail = face <= 3 ? 'pitting before pits are open' : face <= 5 ? 'speeding' : 'improper lug nut installation';
          diceRecords.push({ kind: 'd6', dice: [face], label: 'PIT ERROR specifics' });
          modifiers.push({ name: 'pit error', value: detail, detail: `d6 ${face}` });
        }
        break;
      }

      case 'driverIncident': {
        momentTag ??= 'rare.driver-incident';
        const car = takeIsolation();
        if (!car) break;
        const nb = neighbourOf(car, input);
        diceRecords.push(...nb.dice);
        modifiers.push(...nb.modifiers);
        noteActor(nb.car);
        // "Both drivers roll in their TROUBLE range, resolved using the usual
        // procedure, and are placed on the FEUD list."
        const pair = nb.car ? [car, nb.car] : [car];
        trouble = resolveTrouble({
          field: { top: pair, middle: [], bottom: [] },
          group: 'top',
          turn: input.turn,
          totalTurns: input.totalTurns,
          escapeAdj: input.escapeAdj,
          repairAdj: input.repairAdj,
          dice: input.dice,
          bigOne: false,
          chipPolicy: input.chipPolicy,
          cause: 'DRIVER INCIDENT',
        });
        if (nb.car) feudPairs.push([car.driverId, nb.car.driverId]);
        break;
      }

      case 'pitCrewIncident': {
        momentTag ??= 'rare.pit-crew-incident';
        const car = takeIsolation();
        if (!car) break;
        const nb = neighbourOf(car, input);
        diceRecords.push(...nb.dice);
        modifiers.push(...nb.modifiers);
        noteActor(nb.car);
        if (nb.car) feudPairs.push([car.driverId, nb.car.driverId]);
        break;
      }

      case 'troubleEvent': {
        momentTag ??= 'wreck.trouble';
        let groups: TroubleGroup[];
        if (effect.groups === 'ALL') {
          groups = ['top', 'middle', 'bottom'];
          momentTag = 'wreck.big-one';
        } else if (Array.isArray(effect.groups)) {
          groups = (effect.groups as string[]).map((g) => g.toLowerCase() as TroubleGroup);
        } else if (footnoteTarget) {
          // p.v — for TROUBLE results the race LEADER is part of the TOP group,
          // so a † die of "1 LEADER" rolls the TOP group.
          groups = [footnoteTarget === 'leader' ? 'top' : footnoteTarget];
        } else {
          // AMBIGUITY: SUPER RARE 12 says only "TROUBLE; car debris flies into
          // stands" with no group. Read as the group the RARE roll was made for.
          groups = [input.group];
        }
        trouble = troubleFor(groups, input, 1, active.text);
        break;
      }

      case 'bigTrouble': {
        momentTag ??= 'wreck.big-trouble';
        const group = effectGroup(effect);
        const per = typeof effect.rollsPerDriver === 'number' ? effect.rollsPerDriver : 2;
        trouble = troubleFor(group, input, per, active.text);
        break;
      }

      case 'disqualify': {
        momentTag ??= 'rare.disqualified';
        const car = takeIsolation();
        if (car) sendOut(car, 'disqualified');
        break;
      }

      case 'outOfRace': {
        momentTag ??= 'rare.out-of-race';
        const car = takeIsolation();
        if (car) sendOut(car, active.text);
        break;
      }

      case 'driverDies':
      case 'driverInjured': {
        momentTag ??= effect.type === 'driverDies' ? 'rare.fatality' : 'rare.injury';
        const car = takeIsolation();
        if (car) {
          sendOut(car, active.text);
          pendingEffects.push({ effect, actorIds: [car.driverId], note: 'career-level consequence — the season module applies it' });
          // D-EZ-88 — the typed hand-off. "[dice] races" is rolled HERE, in the
          // race, through the engine's dice (D-RW-1: one d6).
          if (effect.type === 'driverDies') {
            careerConsequences.push({
              driverId: car.driverId,
              kind: 'fatality',
              races: 'season',
              cause: active.text,
              ruleCitation: CITE_SUPER_RARE_CAREER,
              turn: input.turn,
            });
          } else if (effect.duration === 'restOfSeason') {
            careerConsequences.push({
              driverId: car.driverId,
              kind: 'injury',
              races: 'season',
              cause: active.text,
              ruleCitation: CITE_SUPER_RARE_CAREER,
              turn: input.turn,
            });
          } else {
            const die = dice.d(CONSEQUENCE_DICE_SIDES);
            diceRecords.push({ kind: 'd6', dice: [die], label: 'races out injured ([dice] = 1d6, D-RW-1)' });
            modifiers.push({ name: 'races out injured', value: die, detail: 'D-RW-1 — [dice] is one d6' });
            careerConsequences.push({
              driverId: car.driverId,
              kind: 'injury',
              races: die,
              cause: active.text,
              ruleCitation: CITE_SUPER_RARE_CAREER,
              turn: input.turn,
              die,
            });
          }
        }
        break;
      }

      case 'driverSuspended': {
        // SUPER RARE 41/61 — the SAME driver the row's DQ just removed: one row
        // names one man (see `takeIsolation`). The length is "[dice] races".
        momentTag ??= 'rare.suspended';
        const car = takeIsolation();
        if (car) {
          const die = dice.d(CONSEQUENCE_DICE_SIDES);
          diceRecords.push({ kind: 'd6', dice: [die], label: 'races suspended ([dice] = 1d6, D-RW-1)' });
          modifiers.push({ name: 'races suspended', value: die, detail: 'D-RW-1 — [dice] is one d6' });
          careerConsequences.push({
            driverId: car.driverId,
            kind: 'suspension',
            races: die,
            cause: active.text,
            ruleCitation: CITE_SUPER_RARE_CAREER,
            turn: input.turn,
            die,
          });
        }
        break;
      }

      case 'yellowFlag':
        yellowFlag = true;
        yellowTurns = Math.max(yellowTurns, 1);
        momentTag ??= 'rare.caution';
        break;

      case 'lowerPitCrewGrade': {
        momentTag ??= 'rare.pit-crew-injury';
        const car = takeIsolation();
        pendingEffects.push({
          effect,
          actorIds: car ? [car.driverId] : [],
          note: 'duration is not printed (charts.json needsReview NR-009) — the caller decides',
        });
        break;
      }

      case 'rainThreat': {
        momentTag ??= 'rare.rain-threat';
        const r = input.dice.sum2d6();
        diceRecords.push({ kind: '2d6-sum', dice: r.dice, total: r.total, label: 'turns until rain could fall' });
        modifiers.push({ name: 'turns until rain', value: r.total });
        pendingEffects.push({ effect: { ...effect, turnsUntilRain: r.total }, actorIds: [], note: 'RAIN THREAT procedure, RARE RESULTS sheet' });
        break;
      }

      case 'loseAllPerformanceChips':
        momentTag ??= 'rare.chips-lost';
        pendingEffects.push({ effect, actorIds: [], note: 'field-wide — every driver forfeits his chips' });
        break;

      case 'wreckAndFight':
      case 'addToFeudList': {
        momentTag ??= effect.type === 'wreckAndFight' ? 'rare.wreck-and-fight' : 'rare.feud';
        const a = takeIsolation();
        const b = takeIsolation(true);
        if (a && b && a.driverId !== b.driverId) feudPairs.push([a.driverId, b.driverId]);
        pendingEffects.push({
          effect,
          actorIds: [a?.driverId, b?.driverId].filter((x): x is string => Boolean(x)),
          note: 'the fight/clash mechanic itself is not printed — drivers isolated only',
        });
        break;
      }

      default:
        momentTag ??= `rare.${KEBAB(effect.type)}`;
        pendingEffects.push({ effect, actorIds: [], note: 'no printed mechanic — the caller applies it' });
        break;
    }
  }

  if (trouble) {
    diceRecords.push(...trouble.dice);
    outIds.push(...trouble.outIds);
    chipsForfeited += trouble.chipsForfeited;
    positionDeltas.push(...trouble.positionDeltas);
    for (const id of trouble.actorIds) noteActor(allCars(input.field).find((c) => c.driverId === id) ?? null);
    if (trouble.yellowFlag) {
      yellowFlag = true;
      yellowTurns = Math.max(yellowTurns, trouble.yellowTurns);
    }
    if (trouble.collectedIds.length > 0) fillGap = true;
    if (trouble.bigOneTriggered) momentTag = 'wreck.big-one';
  }

  return {
    eventType: 'rare',
    momentTag: momentTag ?? 'rare.no-effect',
    ruleCitation: `${CITE.rare}; ${sourceChart} (${sourceCitation})`,
    actorIds,
    dice: diceRecords,
    modifiers,
    description:
      `${input.cause ? `${input.cause}: ` : ''}${chain.map((s) => `${s.code} "${s.text}"`).join(' → ')}` +
      (actorIds.length > 0 ? ` — ${actorIds.join(', ')}.` : '.') +
      (yellowFlag ? ` The yellow is out for ${yellowTurns} turn${yellowTurns === 1 ? '' : 's'}.` : ''),
    positionDeltas,
    group: input.group,
    chain,
    entryText: active.text,
    finders,
    trouble,
    blackFlagInfraction,
    outIds,
    feudPairs,
    fumeIds,
    yellowFlag,
    yellowTurns,
    chipsForfeited,
    pendingEffects,
    careerConsequences,
    fillGap,
  };
}
